import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useImpactStats } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO = "/photos/nanda-cosmetiques/ing-baobab-hero.jpg";

const StatsGrid = () => {
 const IMPACT_STATS = useImpactStats();
 return (
 <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-12">
 {IMPACT_STATS.map((s, i) => (
 <FadeUp key={i} delay={i * 0.06}>
 <div className="font-serif text-5xl md:text-6xl text-nanda-ink">{s.value}{s.suffix}</div>
 <div className="label-overline text-nanda-muted mt-3">{s.label}</div>
 </FadeUp>
 ))}
 </div>
 );
};

export default function Maison() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const { scrollY } = useScroll();
 const y = useTransform(scrollY, [0, 600], [0, 100]);

 const values = [
 { t: l("Le travail bien fait", "Work done properly", "O trabalho bem feito"), b: l("Le détail compte, même quand personne ne regarde.", "The detail matters, even when no one is watching.", "O detalhe conta, mesmo quando ninguém está a olhar.") },
 { t: l("D'ici, sans folklore", "From here, without folklore", "Daqui, sem folclore"), b: l("Nous venons de la région, nous n'en faisons pas une carte postale.", "We come from the region, we don't turn it into a postcard.", "Somos da região, não a transformamos num postal.") },
 { t: l("La curiosité", "Curiosity", "A curiosidade"), b: l("Chercher ce qui marche vraiment, pas seulement ce qui plaît.", "Looking for what really works, not just what pleases.", "Procurar o que realmente funciona, não apenas o que agrada.") },
 { t: l("Le compte tenu", "Accountability", "A responsabilidade"), b: l("Envers les gens, envers ce qu'on prélève, envers ce qu'on laisse.", "To the people, to what we take, to what we leave behind.", "Para com as pessoas, para com o que se retira, para com o que se deixa.") },
 { t: l("Passer le relais", "Passing the baton", "Passar o testemunho"), b: l("Ce que nous apprenons doit pouvoir servir à d'autres, après nous.", "What we learn must be able to serve others, after us.", "O que aprendemos deve poder servir a outros, depois de nós.") },
 ];

 const links = [
 { to: "/impact", label: l("Impact", "Impact", "Impacto"), desc: l("Producteurs, emplois, projets, nos résultats concrets.", "Producers, jobs, projects, our concrete results.", "Produtores, empregos, projectos, os nossos resultados concretos.") },
 { to: "/partenaires", label: l("Partenaires", "Partners", "Parceiros"), desc: l("Coopératives, laboratoires, institutions.", "Cooperatives, laboratories, institutions.", "Cooperativas, laboratórios, instituições.") },
 { to: "/rejoindre", label: l("Nous rejoindre", "Join us", "Junte-se a nós"), desc: l("Distributeurs, investisseurs, talents.", "Distributors, investors, talents.", "Distribuidores, investidores, talentos.") },
 ];

 return (
 <div data-testid="page-maison">
 {/* Hero */}
 <section className="relative h-[80vh] min-h-[560px] overflow-hidden bg-nanda-ink">
 <motion.div style={{ y }} className="absolute inset-0">
 <img src={HERO} alt={l("La Maison NANDA SÁ", "The NANDA SÁ House", "A Casa NANDA SÁ")} className="h-full w-full object-cover" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/30 via-nanda-ink/15 to-nanda-ink/60" />
 </motion.div>
 <div className="relative z-10 mx-auto max-w-[1400px] h-full px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-20 md:pb-24">
 <div className="label-overline text-white/85 mb-5">{l("La Maison", "The House", "A Casa")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0] max-w-5xl">
 {l("Une Maison. Cinq territoires d'expression.", "One House. Five territories of expression.", "Uma Casa. Cinco territórios de expressão.")}
 </h1>
 </div>
 </section>

 {/* Manifeste, full */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-36">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-10">{l("Le Manifeste", "The Manifesto", "O Manifesto")}</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-balance leading-[1.02]">
 {l("Bâtir une maison, pas seulement une entreprise.", "Building a house, not just a company.", "Construir uma casa, não apenas uma empresa.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.2} className="mt-16 max-w-3xl space-y-6 text-nanda-inkSoft text-[17px] md:text-lg leading-relaxed">
 <p>{l(
 "Une entreprise, à la fin, c'est une manière de dire ce à quoi l'on tient. Nous, ce à quoi nous tenons, c'est un travail bien fait, des savoir-faire qu'on ne laisse pas se perdre, et un territoire dont on ne parle pas seulement le dimanche.",
 "In the end, a company is a way of saying what you care about. What we care about is work done properly, know-how that isn't allowed to fade, and a territory we don't only talk about on Sundays.",
 "No fim, uma empresa é uma forma de dizer aquilo a que nos apegamos. Aquilo a que nos apegamos é um trabalho bem feito, saberes que não se deixam perder, e um território de que não se fala apenas ao domingo."
 )}</p>
 <p>{l(
 "Nous croyons que l'Afrique de l'Ouest n'a pas besoin d'être expliquée. Elle a besoin d'être révélée avec sérieux, sans folklore ni concession sur la qualité. C'est là, dans ce simple exigence, que se construit notre travail au quotidien.",
 "We believe West Africa does not need to be explained. It needs to be revealed with seriousness, without folklore, without cutting corners on quality. It is in that simple demand that our daily work is built.",
 "Acreditamos que a África Ocidental não precisa de ser explicada. Precisa de ser revelada com seriedade, sem folclore, sem cedências na qualidade. É nessa exigência simples que se constrói o nosso trabalho quotidiano."
 )}</p>
 <p>{l(
 "Chaque décision, si modeste soit-elle, dit la même chose : une entreprise qui entreprend, oui, mais qui laisse aussi une trace lisible sur son territoire.",
 "Every decision, however modest, says the same thing: a company that undertakes, yes, but that also leaves a legible trace on its territory.",
 "Cada decisão, por mais modesta que seja, diz a mesma coisa: uma empresa que empreende, sim, mas que também deixa uma marca legível no seu território."
 )}</p>
 <p className="font-serif text-2xl md:text-3xl text-nanda-ink pt-6 italic">{l("Bienvenue chez NANDA SÁ.", "Welcome to NANDA SÁ.", "Bem-vindo à NANDA SÁ.")}</p>
 </FadeUp>

 {/* À propos. Identité corporate */}
 <FadeUp delay={0.3} className="mt-20 pt-14 border-t border-nanda-line max-w-3xl">
 <div className="label-overline text-nanda-muted mb-6">{l("À propos", "About", "Sobre")}</div>
 <div className="space-y-5 text-nanda-inkSoft text-[16px] md:text-[17px] leading-relaxed">
 <p>
 {l(
 "Fondée en 2023, NANDA SÁ est une entreprise du groupe KADIDIA DIOP GROUP SAS.",
 "Founded in 2023, NANDA SÁ is a company of KADIDIA DIOP GROUP SAS.",
 "Fundada em 2023, a NANDA SÁ é uma empresa do grupo KADIDIA DIOP GROUP SAS."
 )}
 </p>
 <p>
 {l(
 "Notre mission est de développer des solutions, des produits et des services durables en Afrique de l'Ouest à travers plusieurs domaines d'activité :",
 "Our mission is to develop sustainable solutions, products and services across West Africa through several areas of activity:",
 "A nossa missão é desenvolver soluções, produtos e serviços sustentáveis na África Ocidental através de vários domínios de actividade:"
 )}
 </p>
 <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-2 text-nanda-ink">
 {[
 l("Énergie", "Energy", "Energia"),
 l("Commerce", "Trade", "Comércio"),
 l("Cosmétiques naturels", "Natural cosmetics", "Cosméticos naturais"),
 l("Formation", "Training", "Formação"),
 l("Entrepreneuriat", "Entrepreneurship", "Empreendedorismo"),
 l("Développement économique", "Economic development", "Desenvolvimento económico"),
 ].map((item, i) => (
 <li key={i} className="flex items-start gap-3" data-testid={`about-domain-${i}`}>
 <span className="text-nanda-terracotta font-serif text-lg leading-none pt-1">·</span>
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Pourquoi ici. Guinée-Bissau */}
 <section className="border-t border-nanda-line bg-nanda-bg">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted mb-4">{l("Pourquoi ici", "Why here", "Porquê aqui")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {l("La Guinée-Bissau. Un carrefour de l'Afrique de l'Ouest.", "Guinea-Bissau. A West African crossroads.", "A Guiné-Bissau. Uma encruzilhada oeste-africana.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8 space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 <p>
 
 {l(
 "région méridionale du célèbre pour ses forêts, ses fleuves et une agriculture traditionnelle d'exception. Un terroir vivant où miel, sel de mangrove, fruits tropicaux et savoir-faire artisanaux se transmettent de génération en génération.",
 "the southern region of renowned for its forests, rivers and exceptional traditional agriculture. A living terroir where honey, mangrove salt, tropical fruits and artisanal know-how are passed on from generation to generation.",
 "a região Guiné-Bissau, famosa pelas suas florestas, rios e uma agricultura tradicional excepcional. Um terroir vivo onde mel, sal de mangue, frutos tropicais e saberes artesanais se transmitem de geração em geração."
 )}
 </p>
 <p>
 <span className="text-nanda-ink font-medium">{l("La Guinée-Bissau", "Guinea-Bissau", "A Guiné-Bissau")}</span>{", "}
 {l(
 "l'un des premiers producteurs mondiaux d'anacarde et d'un patrimoine culturel bissau-guinéen unique. Une nation ouverte sur l'Atlantique, aux ressources naturelles précieuses et à la diaspora créative.",
 "one of the world's leading cashew producers and holder of a unique Bissau-Guinean cultural heritage. A nation open to the Atlantic, with precious natural resources and a creative diaspora.",
 "um dos principais produtores mundiais de caju e detentor de um património cultural bissau-guineense único. Uma nação aberta ao Atlântico, com recursos naturais preciosos e uma diáspora criativa."
 )}
 </p>
 <p>
 <span className="text-nanda-ink font-medium">{l("Notre choix", "Our choice", "A nossa escolha")}</span>{", "}
 {l(
 "NANDA SÁ est née de la conviction que ce carrefour ouest-africain mérite d'être valorisé au niveau international. Nous transformons ici. Nous exportons de là. Nous respectons partout.",
 "NANDA SÁ was born of the conviction that this West African crossroads deserves to be celebrated internationally. We transform here. We export from here. We respect everywhere.",
 "A NANDA SÁ nasceu da convicção de que esta encruzilhada oeste-africana merece ser valorizada a nível internacional. Transformamos aqui. Exportamos daqui. Respeitamos em todo o lado."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Histoire */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-6">
 <div className="aspect-[4/5] overflow-hidden">
 <img
 src="/photos/home-hero.jpg"
 alt={l("NANDA SÁ, architecture contemporaine ouest-africaine", "NANDA SÁ, contemporary West African architecture", "NANDA SÁ, arquitectura contemporânea oeste-africana")}
 className="h-full w-full object-cover"
 loading="lazy"
 data-testid="maison-histoire-image"
 />
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-6 md:pl-8">
 <div className="label-overline text-nanda-muted mb-5">{l("Notre histoire", "Our story", "A nossa história")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-10 leading-[1.05]">{l("D'où nous venons, et pourquoi.", "Where we come from, and why.", "De onde vimos, e porquê.")}</h2>
 <div className="space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 <p>{l(
 "Il y a des entreprises qu'on lance parce qu'une opportunité se présente. D'autres qu'on lance parce qu'on n'arrive plus à ne pas les lancer. NANDA SÁ appartient au second groupe.",
 "Some companies start because an opportunity shows up. Others start because you can no longer keep them inside. NANDA SÁ belongs to the second kind.",
 "Há empresas que se lançam porque surge uma oportunidade. Outras que se lançam porque já não se consegue não as lançar. A NANDA SÁ pertence ao segundo grupo."
 )}</p>
 <p>{l(
 "Entre la Guinée-Bissau, nous travaillons avec les gens qui sont là. Les producteurs, les artisans, les techniciens, les jeunes qu'on forme. Nous n'expliquons pas l'Afrique de l'Ouest, nous en faisons partie.",
 "Between Guinea-Bissau, we work with the people who are there. Producers, artisans, technicians, the young people we train. We do not explain West Africa, we are part of it.",
 "Entre a Guiné-Bissau, trabalhamos com quem lá está. Os produtores, os artesãos, os técnicos, os jovens que formamos. Não explicamos a África Ocidental, fazemos parte dela."
 )}</p>
 <p>{l(
 "Le reste, ce sont des choix concrets. Ce qu'on transforme, comment on le fait, qui l'on paie correctement, ce qu'on refuse aussi.",
 "The rest is a matter of concrete choices. What we transform, how we do it, who we pay properly, and what we refuse too.",
 "O resto é uma questão de escolhas concretas. O que se transforma, como se faz, a quem se paga como deve ser, e também o que se recusa."
 )}</p>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Vision / Mission / Valeurs */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14">
 <div className="label-overline text-nanda-muted mb-4">{l("Notre cap", "Our compass", "O nosso rumo")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl">{l("Là où nous allons, ce à quoi nous tenons.", "Where we're going, what we hold to.", "Para onde vamos, aquilo a que nos apegamos.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 <StaggerItem className="bg-nanda-bg p-10 md:p-14">
 <div className="label-overline text-nanda-muted">{l("Là où nous allons", "Where we're going", "Para onde vamos")}</div>
 <p className="mt-6 font-serif text-2xl md:text-3xl leading-snug text-nanda-ink">{l(
 "Faire compter les marques africaines, sur leur continent et ailleurs. Bâtir un groupe qu'on cite lorsqu'on parle d'Afrique de l'Ouest, non pour ce qu'il produit vite, mais pour ce qu'il garde longtemps.",
 "Making African brands count, on their continent and beyond. Building a group people mention when they speak of West Africa, not for what it makes fast, but for what it keeps over time.",
 "Fazer com que as marcas africanas contem, no seu continente e mais além. Construir um grupo que se cita quando se fala da África Ocidental, não pelo que faz depressa, mas pelo que preserva ao longo do tempo."
 )}</p>
 </StaggerItem>
 <StaggerItem className="bg-nanda-bg p-10 md:p-14">
 <div className="label-overline text-nanda-muted">{l("Ce que nous faisons", "What we do", "O que fazemos")}</div>
 <p className="mt-6 font-serif text-2xl md:text-3xl leading-snug text-nanda-ink">{l(
 "Nous travaillons dans des secteurs qui touchent au quotidien : produits naturels, cosmétiques botaniques, énergie solaire, équipements professionnels. Bientôt la formation et l'art de vivre. À chaque fois, la même exigence.",
 "We work in sectors that touch daily life: natural products, botanical cosmetics, solar energy, professional equipment. Soon training and lifestyle. Every time, the same standard.",
 "Trabalhamos em sectores que tocam o dia-a-dia: produtos naturais, cosméticos botânicos, energia solar, equipamentos profissionais. Em breve, a formação e a arte de viver. De cada vez, a mesma exigência."
 )}</p>
 </StaggerItem>
 </Stagger>

 <div className="mt-px grid grid-cols-1 md:grid-cols-5 gap-px bg-nanda-line border border-nanda-line border-t-0">
 {values.map((v, i) => (
 <FadeUp key={i} delay={i * 0.05} className="bg-nanda-bg p-8 md:p-10">
 <div className="label-overline text-nanda-muted">0{i + 1}</div>
 <div className="font-serif text-2xl md:text-3xl mt-4">{v.t}</div>
 <p className="mt-4 text-nanda-inkSoft text-sm leading-relaxed">{v.b}</p>
 </FadeUp>
 ))}
 </div>
 </div>
 </section>

 {/* Stats */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <FadeUp className="mb-12">
 <div className="label-overline text-nanda-muted mb-4">{l("Empreinte", "Footprint", "Pegada")}</div>
 <h2 className="font-serif text-3xl md:text-5xl">{l("NANDA SÁ en chiffres.", "NANDA SÁ in figures.", "A NANDA SÁ em números.")}</h2>
 </FadeUp>
 <StatsGrid />
 </div>
 </section>

 {/* Links */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {links.map((c) => (
 <Link key={c.to} to={c.to} className="bg-nanda-bg p-10 md:p-12 hover:bg-nanda-bgAlt transition-colors duration-500 group" data-testid={`maison-link-${c.to.slice(1)}`}>
 <div className="label-overline text-nanda-muted">{c.label}</div>
 <p className="mt-4 font-serif text-2xl md:text-3xl leading-tight text-nanda-ink">{c.desc}</p>
 <span className="mt-8 inline-flex items-center gap-2 label-overline text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">{l("Découvrir", "Discover", "Descobrir")} <ArrowUpRight size={14} strokeWidth={1.5} /></span>
 </Link>
 ))}
 </div>
 </section>
 </div>
 );
}
